import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Stack, Tooltip } from "@mui/material";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import SignerInputFields from "./SignerInputFields";
import {
  addApprovers,
  addSigner,
  getContactSuggestion,
} from "../SignRequestHelper";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  selectScreen,
  setReduxPdfApprovals,
  setReduxSignRequest,
  updateIndividualSigner,
} from "redux/appSlice";
import { RootState } from "utils/store";
import { isTruthy } from "helpers/methods";
import { RequestParticipant, Reviewers } from "models/interfaces";
import ApprovalFrom from "./ApprovalFrom";
import DateAndTimeSelector from "./DateAndTimeSelector";
import { StableSignerDropContainer } from "./SignerDndContainers";
import {
  ReviewerDropContainer,
  StableReviewerEmptyDropZone,
} from "./ReviewerDropContainer";
import SignerHeader from "./MobileSignerHeader";
import SignerDragLayer from "./SignerDragLayer";
import ApproverDragLayer from "./ApproverDragLayer";
import SignerLevelHeader from "./SignerLevelHeader";
import SignerDelaySlot from "./SignerDelaySlot";
import SignerNewLevelZone from "./SignerNewLevelZone";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import { InfoCircleGlyph, PersonGlyph } from "./RecipientsIcons";
import approversAndSignersStyles from "../ApproversAndSigners.styles";
import CustomInput from "global/components/CustomInput/CustomInput";
import strings from "global/constants/StringConstants";
import viewpaths from "global/constants/ViewPathConstants";
import { fillCopy, isSmsCreditBalanceLow } from "helpers/smsCreditHelpers";
import history from "utils/history";
import { setErrors } from "redux/appSlice";
import { isEmbedEditorMode } from "embed/EmbedEditorMode";

const styles = approversAndSignersStyles;

const emptySigner = {
  id: 0,
  name: "",
  email: "",
  order: 0,
  color: "",
  cc: false,
};

const emptyReviewer = {
  id: 0,
  name: "",
  email: "",
  order: 0,
  color: "",
};

interface ApproversAndSignersProps {
  /** Credits the account has left. */
  smsCreditBalance?: number;
  /** Credits this send will consume — delivery plus SMS verification. */
  smsCreditCost?: number;
  /** The same, with every allowed OTP resend spent. Equals the cost when nobody verifies by text. */
  smsCreditMaxCost?: number;
  /**
   * The balance has actually loaded and the account can use SMS. Off while it
   * is in flight or unreadable, so the step never states a balance it doesn't
   * have.
   */
  showSmsCreditBanner?: boolean;
}

export default function ApproversAndSigners({
  smsCreditBalance = 0,
  smsCreditCost = 0,
  smsCreditMaxCost = 0,
  showSmsCreditBanner = false,
}: ApproversAndSignersProps) {
  const isMobile = useAppSelector(selectScreen);
  const dispatch = useAppDispatch();
  let { signRequest, contacts, isBulkSend, errors } = useAppSelector(
    (state: RootState) => state.app,
  );
  const [tabValue, setTabValue] = useState<"signer" | "approver">("signer");
  const [openDatePicker, setOpenDatePicker] = useState<boolean>(false);
  const [isScheduleReq, setIsScheduleReq] = useState<boolean>(false);
  const [isDelaySigner, setIsDelaySigner] = useState<boolean>(false);
  const [signerExcludeIds, setSignerExcludeIds] = useState<string[]>([]);
  const [approvalExcludeIds, setApprovalExcludeIds] = useState<string[]>([]);
  const [signerToDelay, setSignerToDelay] = useState<
    Partial<RequestParticipant>
  >({
    id: -1,
    order: -1,
  });
  const [contactList, setContactList] = useState<any>({
    name: {},
    email: {},
  });

  const reviewers = useMemo(
    () => signRequest?.approval?.reviewers ?? [],
    [signRequest?.approval?.reviewers],
  );

  const groupWiseSigner: Record<string, RequestParticipant[]> = useMemo(() => {
    return signRequest?.signers?.reduce(
      (acc: Record<number, any[]>, signer: RequestParticipant) => {
        if (!acc[signer.order]) {
          acc[signer.order] = [];
        }
        acc[signer.order].push(signer);
        return acc;
      },
      {} as Record<string, RequestParticipant[]>,
    );
  }, [signRequest?.signers]);

  useEffect(() => {
    const emailsInUse = (signRequest.signers ?? [])
      .map((s: RequestParticipant) => s.email)
      .filter(Boolean);
    setSignerExcludeIds(emailsInUse);
    setContactList({ name: {}, email: {} });
  }, [signRequest.signers]);

  useEffect(() => {
    const emailsInUse = (signRequest.approval?.reviewers ?? [])
      .map((r: Reviewers) => r.email)
      .filter(Boolean);
    setApprovalExcludeIds(emailsInUse);
    setContactList({ name: {}, email: {} });
  }, [signRequest.approval?.reviewers]);

  useEffect(() => {
    if (!isTruthy(signRequest.signers || signRequest.signers?.length === 0)) {
      addSigner(emptySigner, signRequest.signers ?? [], dispatch, false);
    }
    // if (
    //   !isTruthy(signRequest.approval) ||
    //   !isTruthy(
    //     signRequest.approval.reviewers ||
    //       signRequest.approval.reviewers?.length === 0
    //   )
    // ) {
    //   addApprovers(emptyReviewer, reviewers, dispatch);
    // }
  }, []);

  const handleAddSigner = async () => {
    addSigner(emptySigner, signRequest.signers ?? [], dispatch, false);
    if (contacts.length < 10) {
      getContactSuggestion("", dispatch);
    }
    getContactSuggestion(" ", dispatch, signerExcludeIds);
  };

  /** "Add signer here" on a level header — drops the new signer into that level. */
  const handleAddSignerToLevel = (order: number) => {
    const created = addSigner(
      emptySigner,
      signRequest.signers ?? [],
      dispatch,
      false,
    );
    if (created?.id !== undefined) {
      dispatch(
        updateIndividualSigner({
          signerId: created.id,
          fieldsToUpdate: { order },
        }),
      );
    }
    if (contacts.length < 10) {
      getContactSuggestion("", dispatch);
    }
    getContactSuggestion(" ", dispatch, signerExcludeIds);
  };

  /** "CC recipient" entry of the add menu — a signer that only gets a copy. */
  const handleAddCc = async () => {
    addSigner(
      { ...emptySigner, cc: true },
      signRequest.signers ?? [],
      dispatch,
      false,
    );
    if (contacts.length < 10) {
      getContactSuggestion("", dispatch);
    }
    getContactSuggestion(" ", dispatch, signerExcludeIds);
  };

  const handleAdd = (group: boolean, groupName?: string) => {
    addApprovers(emptyReviewer, reviewers, dispatch, group, groupName);
    if (contacts.length < 10) {
      getContactSuggestion("", dispatch);
    }
  };

  const handleAddApprover = () => {
    handleAdd(false);
    getContactSuggestion(" ", dispatch, approvalExcludeIds);
  };

  const handleAddGroup = () => {
    handleAdd(true);
    getContactSuggestion(" ", dispatch, approvalExcludeIds);
  };

  // const handleDeleteApprover = (email: string) => {
  //   const updatedApprover = reviewers.filter(
  //     (approver: any) => approver.email !== email,
  //   );
  //   dispatch(setReduxPdfApprovals({ reviewers: updatedApprover }));
  //   setApprovalExcludeIds((prevExcludeIds) =>
  //     prevExcludeIds.filter((id) => id !== email),
  //   );
  // };

  // New Logic (ID based)
  const handleDeleteApprover = (id: number, email: string) => {
    const updatedApprover = reviewers.filter(
      (approver: any) => approver.id !== id,
    );
    dispatch(setReduxPdfApprovals({ reviewers: updatedApprover }));
    const isEmailStillExists = updatedApprover.some(
      (r: any) => r.email === email,
    );
    if (!isEmailStillExists) {
      setApprovalExcludeIds((prevExcludeIds) =>
        prevExcludeIds.filter((exId) => exId !== email),
      );
    }
  };

  const getHeader = () => {
    return (
      <SignerHeader
        tabValue={tabValue}
        setTabValue={setTabValue}
        signers={signRequest.signers}
        isBulkSend={isBulkSend}
        signerOrderRequired={signRequest.signerOrderRequired}
        approvalOrderNeeded={signRequest.approval.orderNeeded}
        reviewers={reviewers}
        isMobile={isMobile}
        onSignerOrderChange={(checked) => {
          dispatch(
            setReduxSignRequest({
              signerOrderRequired: checked,
              signers: signRequest.signers.map(
                (signer: any, index: number) => ({
                  ...signer,
                  order: index + 1,
                  ...(!checked && index === 0
                    ? {
                        delayDays: undefined,
                        delayHours: undefined,
                        scheduleTime: undefined,
                      }
                    : {}),
                }),
              ),
            }),
          );
        }}
        onApproverOrderChange={(checked) => {
          dispatch(setReduxPdfApprovals({ orderNeeded: checked }));
        }}
        onAddSigner={handleAddSigner}
        onAddApprover={handleAddApprover}
        onAddCc={handleAddCc}
        onAddGroup={handleAddGroup}
      />
    );
  };

  const signerGroups = useMemo(() => {
    return Object.values(groupWiseSigner || {});
  }, [groupWiseSigner]);

  /**
   * Contact-suggestion lists are keyed per row, so each signer needs its own
   * index even when several share a level.
   */
  const signerIndexById = useMemo(() => {
    const map = new Map<number, number>();
    (signRequest?.signers ?? []).forEach(
      (signer: RequestParticipant, index: number) => map.set(signer.id, index),
    );
    return map;
  }, [signRequest?.signers]);

  const reviewerRenderList = useMemo(() => {
    if (!reviewers?.length) return [];

    // Step 1 — group by order
    const orderMap = new Map<number, Reviewers[]>();

    reviewers.forEach((r: any) => {
      const order = Number(r.order);
      if (!orderMap.has(order)) orderMap.set(order, []);
      orderMap.get(order)!.push(r);
    });

    // Step 2 — stable order rows
    const rows = Array.from(orderMap.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([order, members], index) => {
        // identity must stay stable
        const first = members[0];

        const identity = first.groupId
          ? `group-${first.groupId}`
          : `reviewer-${first.id}`;

        return {
          identity,
          members,
          visualOrder: index + 1,
        };
      });

    return rows;
  }, [reviewers]);

  useEffect(() => {
    const signerEmailSet = new Set(
      (signRequest.signers || [])
        .map((signer: RequestParticipant) => signer.email)
        .filter((email: string) => isTruthy(email)),
    );

    setSignerExcludeIds((prev) => prev.filter((id) => signerEmailSet.has(id)));
  }, [signRequest.signers]);

  useEffect(() => {
    const reviewerEmailSet = new Set(
      (reviewers || [])
        .map((reviewer: Reviewers) => reviewer.email)
        .filter((email: string) => isTruthy(email)),
    );

    setApprovalExcludeIds((prev) =>
      prev.filter((id) => reviewerEmailSet.has(id)),
    );
  }, [reviewers]);

  const hasInPersonSigner = (signRequest.signers ?? []).some(
    (signer: RequestParticipant) =>
      !signer.cc && signer.deliveryMode === strings.deliveryModes.inPerson,
  );

  const handleHostEmailChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value.substring(0, 256);
    dispatch(
      setReduxSignRequest({
        signRequestConfig: {
          ...signRequest.signRequestConfig,
          inPersonHostEmail: value,
        },
      }),
    );
    dispatch(setErrors({ ...errors, inPersonHostEmail: "" }));
  };

  const renderHostEmailBanner = () =>
    !isEmbedEditorMode() &&
    hasInPersonSigner && (
      <Box sx={styles.hostEmailBanner}>
        <PersonGlyph sx={styles.hostEmailIcon} />
        <Box component="span" sx={styles.hostEmailLabel}>
          Host email
        </Box>
        <Tooltip
          describeChild
          title="The email of whoever will hand their device to each in-person signer."
          arrow
        >
          <InfoCircleGlyph sx={styles.hostEmailInfoIcon} />
        </Tooltip>
        <Box sx={styles.hostEmailField}>
          <CustomInput
            id="in-person-host-email"
            placeHolder="host@example.com"
            value={signRequest.signRequestConfig?.inPersonHostEmail ?? ""}
            onChange={handleHostEmailChange}
            error={errors.inPersonHostEmail}
            size="small"
            propsToInputElement={{ "aria-label": "Host email", maxLength: 256 }}
          />
        </Box>
      </Box>
    );

  /**
   * What this send will cost in SMS credits, and whether that is a problem.
   *
   * Three reasons to speak up, and they are not the same: this send doesn't
   * fit (blocking — the server refuses the whole request, no email and no
   * text), resends could empty the wallet part-way through (advisory — the
   * quoted cost assumes every signer gets their code first time), or the
   * wallet is merely low (advisory — a sequential request whose later levels
   * open days from now can find it empty, and nobody is at a screen to see
   * that fail). Silent below all three, because a healthy balance is not news.
   *
   * Only the first disables Send: the ceiling is what the sender *can* be
   * billed, not what the server checks, so blocking on it would refuse sends
   * that would have gone through.
   */
  const renderSmsCreditBanner = () => {
    if (isEmbedEditorMode() || !showSmsCreditBanner || smsCreditCost === 0) return null;

    const isBlocked = smsCreditBalance < smsCreditCost;
    const maxCost = Math.max(smsCreditMaxCost, smsCreditCost);
    const hasResendHeadroom = maxCost > smsCreditCost;
    if (!isSmsCreditBalanceLow(smsCreditBalance, smsCreditCost, maxCost))
      return null;

    const headroomNote = hasResendHeadroom
      ? ` ${fillCopy(strings.smsCredits.resendHeadroom, {
          resends: strings.SMS_OTP_MAX_RESENDS,
          max: maxCost,
        })}`
      : "";

    const message = isBlocked
      ? `${fillCopy(strings.smsCredits.sendCostShort, {
          cost: smsCreditCost,
          balance: smsCreditBalance,
        })}${headroomNote}`
      : `${fillCopy(strings.smsCredits.sendCost, {
          cost: smsCreditCost,
          balance: smsCreditBalance,
        })} ${fillCopy(strings.smsCredits.lowBalance, {
          balance: smsCreditBalance,
        })}${headroomNote}`;

    return (
      <Box sx={styles.smsCreditBanner(isBlocked)} role="status">
        <SmsOutlinedIcon sx={styles.smsCreditIcon(isBlocked)} />
        <Box component="span" sx={styles.smsCreditText(isBlocked)}>
          {message}
        </Box>
        <Box
          component="button"
          type="button"
          onClick={() => history.push(viewpaths.viewMyPlanViewPath)}
          sx={styles.smsCreditLink}
        >
          {strings.smsCredits.buyMore}
        </Box>
      </Box>
    );
  };

  const renderSignerContent = () => {
    const hasSigners =
      isTruthy(signRequest.signers) && signRequest.signers?.length > 0;

    if (!hasSigners) {
      return (
        <Box sx={styles.emptyBlock}>
          <Box component="span" sx={styles.emptyTitle}>
            No signers yet
          </Box>
          <Button
            disableRipple
            onClick={handleAddSigner}
            sx={styles.emptyButton}
          >
            Add a signer
          </Button>
        </Box>
      );
    }

    return (
      <Box sx={{ width: "100%", pb: isMobile ? 9 : 0 }}>
        {signRequest.signerOrderRequired && (
          <Box sx={styles.orderHint}>
            Signers in the same level are notified together. Levels run one
            after another — mix them freely for sequential, parallel, or hybrid
            flows.
          </Box>
        )}

        {renderSmsCreditBanner()}

        {renderHostEmailBanner()}

        {!isBulkSend &&
          signerGroups.map((group: RequestParticipant[], index: number) => {
            const signer = group[0];
            const isLast = index === signerGroups.length - 1;
            const nextGroup = signerGroups[index + 1];

            return (
              /*
               * The whole level is one drop target — header, rows and the wait
               * slot all sit inside it so the drag highlight wraps the level,
               * exactly as the design's group container does. A level is simply
               * a list of signer rows; there is no separate "grouped signer"
               * treatment.
               */
              <StableSignerDropContainer
                key={`group-${group[0].id}`}
                orderKey={signer.order}
                signers={signRequest.signers}
                dispatch={dispatch}
              >
                {signRequest.signerOrderRequired && (
                  <SignerLevelHeader
                    levelIndex={index}
                    levelCount={signerGroups.length}
                    order={Number(signer.order)}
                    signers={signRequest.signers}
                    dispatch={dispatch}
                    onAddToLevel={handleAddSignerToLevel}
                  />
                )}

                {group.map((member: RequestParticipant) => (
                  <SignerInputFields
                    key={member.id}
                    eachSigner={member}
                    groupSigner={group}
                    index={signerIndexById.get(member.id) ?? 0}
                    excludeIds={signerExcludeIds}
                    setExcludeIds={setSignerExcludeIds}
                    signerToDelay={signerToDelay}
                    setSignerToDelay={setSignerToDelay}
                    setIsDelaySigner={setIsDelaySigner}
                    contactList={contactList}
                    setContactList={setContactList}
                    signerOrderRequired={signRequest.signerOrderRequired}
                    signers={signRequest.signers}
                    isMobile={isMobile}
                  />
                ))}

                {/* The wait belongs to the level that runs after the gap. */}
                {signRequest.signerOrderRequired && !isLast && nextGroup && (
                  <SignerDelaySlot
                    signers={nextGroup}
                    dispatch={dispatch}
                    onEdit={() => {
                      setSignerToDelay(nextGroup.filter((s) => !s.cc) as any);
                      setIsDelaySigner(true);
                    }}
                  />
                )}
              </StableSignerDropContainer>
            );
          })}

        {signRequest.signerOrderRequired && !isBulkSend && (
          <SignerNewLevelZone
            signers={signRequest.signers}
            dispatch={dispatch}
          />
        )}
      </Box>
    );
  };

  const renderApproverContent = () => {
    return (
      <>
        <Box sx={styles.approversBanner}>
          <InfoCircleGlyph sx={styles.approversBannerIcon} />
          <Box component="span" sx={styles.approversBannerText}>
            Approvers review and approve the document before it is sent to
            signers.
          </Box>
        </Box>

        {reviewers.length === 0 ? (
          <Box sx={styles.emptyBlock}>
            <Box component="span" sx={styles.emptyTitle}>
              No approvers — the document will go straight to signers
            </Box>
            <Box sx={{ display: "flex", gap: "10px" }}>
              <Button
                disableRipple
                onClick={handleAddApprover}
                sx={styles.emptyButton}
              >
                Add approver
              </Button>
              <Button
                disableRipple
                onClick={handleAddGroup}
                sx={styles.emptyButton}
              >
                Add group
              </Button>
            </Box>
          </Box>
        ) : (
          <Stack direction="row" alignItems="center" spacing={0}>
            <Box
              sx={{
                ...(styles.approversStack as object),
                pb: isMobile ? 6 : 0,
              }}
            >
              {isTruthy(reviewers) &&
                reviewerRenderList.map((item, index) => {
                  const group = item.members;
                  // const eachReviewer = item.identity;
                  const reviewer = group[0];
                  const isDnDGroup = !!reviewer.group;
                  const isLast = index === reviewerRenderList.length - 1;

                  return (
                    <React.Fragment key={reviewer.id}>
                      <StableReviewerEmptyDropZone
                        reviewers={signRequest.approval.reviewers}
                        dispatch={dispatch}
                        baseOrder={reviewer.order}
                      />
                      <ReviewerDropContainer
                        orderKey={reviewer.order}
                        index={index}
                        group={group}
                        orderNeeded={signRequest.approval.orderNeeded}
                        allReviewers={reviewers}
                        dispatch={dispatch}
                      >
                        <Box
                          sx={{
                            width: "100%",
                            position: "relative",
                          }}
                        >
                          <ApprovalFrom
                            allApprovers={signRequest.approval.reviewers}
                            eachApprovers={reviewer}
                            handleAdd={handleAdd}
                            index={index}
                            group={group}
                            identity={item.identity}
                            isDnDGroup={isDnDGroup}
                            contactList={contactList}
                            setContactList={setContactList}
                            excludeIds={approvalExcludeIds}
                            setExcludeIds={setApprovalExcludeIds}
                            // itemType={ItemType.APPROVER}
                            handleDelete={handleDeleteApprover}
                          />
                        </Box>
                      </ReviewerDropContainer>
                      {isLast && (
                        <StableReviewerEmptyDropZone
                          reviewers={signRequest.approval.reviewers}
                          dispatch={dispatch}
                          baseOrder={reviewer.order + 1}
                          isLast
                        />
                      )}
                    </React.Fragment>
                  );
                })}
            </Box>
          </Stack>
        )}
      </>
    );
  };

  return (
    <>
      <DndProvider backend={HTML5Backend}>
        <SignerDragLayer
          enabled={Boolean(isMobile && tabValue === "signer")}
          isMobile={isMobile}
        />
        <ApproverDragLayer
          enabled={Boolean(isMobile && tabValue === "approver")}
          isMobile={isMobile}
        />
        <Box sx={isMobile ? { px: 2, pt: 2, pb: 2 } : styles.page}>
          <Box component="h1" sx={styles.visuallyHidden}>
            Approvers and signers
          </Box>
          {getHeader()}
          {tabValue === "signer"
            ? renderSignerContent()
            : renderApproverContent()}
        </Box>
      </DndProvider>
      <DateAndTimeSelector
        openDatePicker={openDatePicker}
        isDelaySigner={isDelaySigner}
        setOpenDatePicker={setOpenDatePicker}
        setIsDelaySigner={setIsDelaySigner}
        signerToDelay={signerToDelay}
        isScheduleReq={isScheduleReq}
        setIsScheduleRequest={setIsScheduleReq}
        isMobile={isMobile}
      />
    </>
  );
}
